import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Footer from '../../components/footer/index';

import { 
  CreditCardIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  DevicePhoneMobileIcon,
  QrCodeIcon,
  ShieldCheckIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  ShareIcon,
  XMarkIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';

const  PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get bill data from navigation state or use fallback
  const [billData] = useState(location.state?.billData || {
    _id: 'INV-2024-002',
    invoiceNumber: 'INV-2024-002',
    packageSnapshot: {
      name: 'Internet Dedicated 50 Mbps',
      type: 'broadband',
      speed: '50 Mbps'
    },
    customer: {
      fullName: 'PT. Digital Indonesia',
      phone: '081234567890',
      email: 'admin@digital.co.id'
    },
    pricing: {
      amount: 2500000,
      tax: 250000,
      totalAmount: 2750000,
      discount: 125000
    },
    billing: {
      period: 'Februari 2024',
      dueDate: '2024-02-15'
    }
  });

  const [selectedMethod, setSelectedMethod] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [selectedEwallet, setSelectedEwallet] = useState('');
  const [paymentStep, setPaymentStep] = useState('select'); // select, process, success
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(900); // 15 minutes
  const [paymentCode, setPaymentCode] = useState('');
  const [qrCodeData, setQrCodeData] = useState('');

  // Enhanced payment methods with better structure
  const paymentMethods = [
    {
      id: 'bank_transfer',
      name: 'Transfer Bank',
      description: 'Transfer langsung ke rekening bank',
      icon: <BuildingLibraryIcon className="h-6 w-6" />,
      fee: 0,
      processingTime: '1-15 menit',
      banks: [
        { 
          code: 'BCA', 
          name: 'Bank Central Asia', 
          account: '1234567890',
          accountName: 'PT. TELNET INDONESIA'
        },
        { 
          code: 'BNI', 
          name: 'Bank Negara Indonesia', 
          account: '0987654321',
          accountName: 'PT. TELNET INDONESIA'
        },
        { 
          code: 'BRI', 
          name: 'Bank Rakyat Indonesia', 
          account: '5678901234',
          accountName: 'PT. TELNET INDONESIA'
        },
        { 
          code: 'MANDIRI', 
          name: 'Bank Mandiri', 
          account: '4321098765',
          accountName: 'PT. TELNET INDONESIA'
        }
      ]
    },
    {
      id: 'virtual_account',
      name: 'Virtual Account',
      description: 'Bayar melalui Virtual Account Bank',
      icon: <CreditCardIcon className="h-6 w-6" />,
      fee: 2500,
      processingTime: 'Real-time',
      banks: [
        { code: 'BCA', name: 'BCA Virtual Account' },
        { code: 'BNI', name: 'BNI Virtual Account' },
        { code: 'BRI', name: 'BRI Virtual Account' },
        { code: 'MANDIRI', name: 'Mandiri Virtual Account' },
        { code: 'CIMB', name: 'CIMB Virtual Account' },
        { code: 'PERMATA', name: 'Permata Virtual Account' }
      ]
    },
    {
      id: 'e_wallet',
      name: 'E-Wallet',
      description: 'Bayar dengan dompet digital',
      icon: <DevicePhoneMobileIcon className="h-6 w-6" />,
      fee: 0,
      processingTime: 'Real-time',
      options: [
        { code: 'GOPAY', name: 'GoPay', icon: '💚', color: 'bg-green-500' },
        { code: 'OVO', name: 'OVO', icon: '💜', color: 'bg-purple-500' },
        { code: 'DANA', name: 'DANA', icon: '💙', color: 'bg-blue-500' },
        { code: 'LINKAJA', name: 'LinkAja', icon: '❤️', color: 'bg-red-500' }
      ]
    },
    {
      id: 'qris',
      name: 'QRIS',
      description: 'Scan QR Code untuk pembayaran',
      icon: <QrCodeIcon className="h-6 w-6" />,
      fee: 0,
      processingTime: 'Real-time'
    },
    {
      id: 'retail',
      name: 'Gerai Retail',
      description: 'Bayar di minimarket terdekat',
      icon: <BanknotesIcon className="h-6 w-6" />,
      fee: 2500,
      processingTime: '5-30 menit',
      outlets: [
        { code: 'INDOMARET', name: 'Indomaret', color: 'bg-yellow-500' },
        { code: 'ALFAMART', name: 'Alfamart', color: 'bg-red-500' },
        { code: 'LAWSON', name: 'Lawson', color: 'bg-blue-500' },
        { code: 'DAN_DAN', name: 'Dan+Dan', color: 'bg-green-500' }
      ]
    }
  ];

  // Countdown timer effect
  useEffect(() => {
    let timer;
    if (paymentStep === 'process' && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      // Payment expired
      navigate('/billing', { 
        state: { 
          message: 'Waktu pembayaran telah habis. Silakan coba lagi.',
          type: 'warning'
        }
      });
    }
    return () => clearInterval(timer);
  }, [paymentStep, countdown, navigate]);

  // Utility functions
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const generatePaymentCode = (method, bank = '') => {
    const prefixes = {
      bank_transfer: '88001',
      virtual_account: '12345',
      retail: '99887'
    };
    const prefix = prefixes[method] || '00000';
    const timestamp = Date.now().toString().slice(-6);
    return prefix + timestamp + (bank ? bank.slice(0, 2).toUpperCase() : '');
  };

  const generateQRCode = () => {
    // Simulate QR code generation
    const qrData = `telnet-payment-${Date.now()}-${billData._id}`;
    setQrCodeData(qrData);
    return qrData;
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      alert(`${type} berhasil disalin!`);
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert(`${type} berhasil disalin!`);
    }
  };

  const handlePaymentMethodSelect = (method) => {
    setSelectedMethod(method.id);
    const code = generatePaymentCode(method.id, selectedBank);
    setPaymentCode(code);
    
    if (method.id === 'qris') {
      generateQRCode();
    }
  };

  const processPayment = async () => {
    setLoading(true);
    
    // Simulate API call to create payment
    try {
      // Here you would normally make an API call to your payment service
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const finalPaymentCode = generatePaymentCode(selectedMethod, selectedBank || selectedEwallet);
      setPaymentCode(finalPaymentCode);
      setLoading(false);
      setPaymentStep('process');
      
      // Start countdown
      setCountdown(900); // 15 minutes
      
    } catch (error) {
      console.error('Payment processing failed:', error);
      setLoading(false);
      alert('Gagal memproses pembayaran. Silakan coba lagi.');
    }
  };

  const simulatePaymentSuccess = () => {
    setPaymentStep('success');
  };

  const selectedMethodData = paymentMethods.find(m => m.id === selectedMethod);
  const totalWithFee = (billData.pricing?.totalAmount || 0) + (selectedMethodData?.fee || 0);

  // Success page
  if (paymentStep === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 pt-8">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircleIcon className="w-8 h-8 text-green-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Pembayaran Berhasil!
            </h1>
            <p className="text-sm text-gray-600 mb-6">
              Tagihan {billData.invoiceNumber} telah berhasil dibayar
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Dibayar:</span>
                  <span className="text-lg font-bold text-green-600">
                    {formatCurrency(totalWithFee)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Metode:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {selectedMethodData?.name}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Waktu:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {new Date().toLocaleString('id-ID')}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Ref ID:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {paymentCode}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button 
                onClick={() => navigate('/billing')}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
              >
                Kembali ke Tagihan
              </button>
              <button className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2">
                <DocumentTextIcon className="w-4 h-4" />
                <span>Unduh Bukti</span>
              </button>
              <button className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2">
                <ShareIcon className="w-4 h-4" />
                <span>Bagikan</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Processing page
  if (paymentStep === 'process') {
    const selectedBankData = selectedMethodData?.banks?.find(b => b.code === selectedBank);
    
    return (
      <div className="min-h-screen bg-gray-50 pt-8">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center mb-6">
            <button 
              onClick={() => setPaymentStep('select')}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors mr-4"
            >
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Menunggu Pembayaran
              </h1>
              <p className="text-sm text-gray-600">
                {billData.invoiceNumber} • {selectedMethodData?.name}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Payment Instructions */}
            <div className="lg:col-span-2 space-y-6">
              {/* Countdown Timer */}
              <div className="bg-white border rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <ClockIcon className="w-6 h-6 text-orange-500" />
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Batas Waktu Pembayaran
                      </h3>
                      <p className="text-sm text-gray-600">
                        Selesaikan pembayaran sebelum waktu habis
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${countdown < 300 ? 'text-red-600' : 'text-orange-500'}`}>
                      {formatTime(countdown)}
                    </div>
                    <p className="text-xs text-gray-600">
                      Menit : Detik
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Instructions */}
              <div className="bg-white border rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Cara Pembayaran
                </h3>
                
                {/* Bank Transfer Instructions */}
                {selectedMethod === 'bank_transfer' && selectedBankData && (
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center mb-4">
                        <span className="font-medium text-gray-900">
                          {selectedBankData.name}
                        </span>
                        <button 
                          onClick={() => setSelectedBank('')}
                          className="text-blue-600 hover:text-blue-700 text-sm"
                        >
                          Ganti Bank
                        </button>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Nomor Rekening:</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-mono font-bold text-gray-900">
                              {selectedBankData.account}
                            </span>
                            <button 
                              onClick={() => copyToClipboard(selectedBankData.account, 'Nomor rekening')}
                              className="p-1 text-blue-600 hover:text-blue-700"
                            >
                              <DocumentDuplicateIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Atas Nama:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {selectedBankData.accountName}
                          </span>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Jumlah Transfer:</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-bold text-blue-600">
                              {formatCurrency(totalWithFee)}
                            </span>
                            <button 
                              onClick={() => copyToClipboard(totalWithFee.toString(), 'Jumlah transfer')}
                              className="p-1 text-blue-600 hover:text-blue-700"
                            >
                              <DocumentDuplicateIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-900">Langkah-langkah:</h4>
                      <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
                        <li>Buka aplikasi mobile banking atau internet banking</li>
                        <li>Pilih menu Transfer ke Bank Lain</li>
                        <li>Masukkan nomor rekening tujuan: <strong>{selectedBankData.account}</strong></li>
                        <li>Masukkan nominal: <strong>{formatCurrency(totalWithFee)}</strong></li>
                        <li>Konfirmasi transfer dan simpan bukti pembayaran</li>
                      </ol>
                    </div>
                  </div>
                )}

                {/* Virtual Account Instructions */}
                {selectedMethod === 'virtual_account' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-gray-900">
                          Nomor Virtual Account
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xl font-mono font-bold text-gray-900">
                          {paymentCode}
                        </span>
                        <button 
                          onClick={() => copyToClipboard(paymentCode, 'Virtual Account')}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                        >
                          Salin
                        </button>
                      </div>
                    </div>
                    
                    {selectedBank && (
                      <div className="p-4 border-2 border-blue-200 bg-blue-50 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-2">
                          Cara Bayar via {selectedBank}
                        </h4>
                        <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                          <li>Buka aplikasi {selectedBank} atau ATM</li>
                          <li>Pilih menu Virtual Account</li>
                          <li>Masukkan nomor: {paymentCode}</li>
                          <li>Konfirmasi pembayaran</li>
                        </ol>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-3 gap-2">
                      {selectedMethodData.banks.map(bank => (
                        <button 
                          key={bank.code}
                          onClick={() => setSelectedBank(bank.code)}
                          className={`p-3 border rounded-lg text-center text-sm font-medium transition-colors ${
                            selectedBank === bank.code
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-900'
                          }`}
                        >
                          {bank.code}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* QRIS Instructions */}
                {selectedMethod === 'qris' && (
                  <div className="text-center space-y-4">
                    <div className="inline-block p-4 bg-gray-50 rounded-lg">
                      <div className="w-48 h-48 bg-gradient-to-br from-blue-400 to-purple-600 mx-auto flex items-center justify-center text-white font-bold rounded-lg">
                        <div className="text-center">
                          <QrCodeIcon className="w-16 h-16 mx-auto mb-2" />
                          <p className="text-xs">QRIS Code</p>
                          <p className="text-xs opacity-80">{paymentCode}</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      Scan QR Code menggunakan aplikasi e-wallet atau mobile banking
                    </p>
                    <div className="flex justify-center space-x-4">
                      <button 
                        onClick={() => copyToClipboard(qrCodeData, 'QRIS Data')}
                        className="text-blue-600 hover:text-blue-700 text-sm underline"
                      >
                        Salin Data QR
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Summary & Status */}
            <div className="space-y-6">
              <div className="bg-white border rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Ringkasan Pembayaran
                </h3>
                
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tagihan:</span>
                    <span className="font-medium">{billData.invoiceNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Layanan:</span>
                    <span className="font-medium">{billData.packageSnapshot?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Periode:</span>
                    <span className="font-medium">{billData.billing?.period}</span>
                  </div>
                  <hr />
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total:</span>
                    <span className="text-lg font-bold text-gray-900">
                      {formatCurrency(totalWithFee)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Status Check */}
              <div className="bg-white border rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Status Pembayaran</h3>
                  <button 
                    onClick={() => window.location.reload()}
                    className="text-blue-600 hover:text-blue-700 text-sm"
                  >
                    Refresh
                  </button>
                </div>
                
                <div className="flex items-center space-x-3 mb-4">
                  <ClockIcon className="w-5 h-5 text-yellow-500" />
                  <span className="text-sm text-gray-600">
                    Menunggu pembayaran...
                  </span>
                </div>
                
                <button 
                  onClick={simulatePaymentSuccess}
                  className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors text-sm"
                >
                  ✓ Simulasi Berhasil
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main payment selection page
  return (
    <div className="min-h-screen bg-gray-50 pt-8">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center mb-8">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors mr-4"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Pembayaran Tagihan
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Pilih metode pembayaran untuk {billData.invoiceNumber}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Payment Methods */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Metode Pembayaran
              </h2>
              
              <div className="space-y-3">
                {paymentMethods.map((method) => (
                  <div 
                    key={method.id}
                    onClick={() => handlePaymentMethodSelect(method)}
                    className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      selectedMethod === method.id
                        ? 'border-green-600 bg-green-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`p-2 rounded-lg ${
                          selectedMethod === method.id 
                            ? 'bg-green-600 text-white' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {method.icon}
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {method.name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {method.description}
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="flex items-center space-x-2">
                          {method.fee > 0 && (
                            <span className="text-sm font-medium text-orange-600">
                              +{formatCurrency(method.fee)}
                            </span>
                          )}
                          <div className={`w-4 h-4 rounded-full border-2 ${
                            selectedMethod === method.id 
                              ? 'border-green-600 bg-green-600' 
                              : 'border-gray-300'
                          }`}>
                            {selectedMethod === method.id && (
                              <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {method.processingTime}
                        </p>
                      </div>
                    </div>
                    
                    {/* Sub-options */}
                    {selectedMethod === method.id && method.banks && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600 mb-2">Pilih Bank:</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {method.banks.map((bank) => (
                            <button 
                              key={bank.code}
                              onClick={() => setSelectedBank(bank.code)}
                              className={`p-2 border rounded-lg text-sm font-medium transition-colors ${
                                selectedBank === bank.code
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-900'
                              }`}
                            >
                              {bank.code}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedMethod === method.id && method.options && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600 mb-2">Pilih E-Wallet:</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {method.options.map((option) => (
                            <button 
                              key={option.code}
                              onClick={() => setSelectedEwallet(option.code)}
                              className={`p-3 border rounded-lg text-center text-sm font-medium transition-colors ${
                                selectedEwallet === option.code
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-900'
                              }`}
                            >
                              <div className="text-lg mb-1">{option.icon}</div>
                              {option.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedMethod === method.id && method.outlets && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600 mb-2">Gerai yang tersedia:</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {method.outlets.map((outlet) => (
                            <div 
                              key={outlet.code}
                              className="p-2 border border-gray-300 bg-gray-50 rounded text-center text-xs font-medium text-gray-900"
                            >
                              {outlet.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Security Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <ShieldCheckIcon className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900">
                    Pembayaran Aman
                  </h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Semua transaksi dilindungi dengan enkripsi SSL 256-bit dan diproses melalui gateway pembayaran terpercaya.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Ringkasan Tagihan
              </h3>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Nomor Tagihan</p>
                  <p className="font-medium text-gray-900">
                    {billData.invoiceNumber}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Layanan</p>
                  <p className="font-medium text-gray-900">
                    {billData.packageSnapshot?.name}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Periode</p>
                  <p className="font-medium text-gray-900">
                    {billData.billing?.period}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Pelanggan</p>
                  <p className="font-medium text-gray-900">
                    {billData.customer?.fullName}
                  </p>
                </div>
                
                <hr className="border-gray-200" />
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Biaya Layanan</span>
                    <span className="text-sm text-gray-900">
                      {formatCurrency(billData.pricing?.amount)}
                    </span>
                  </div>
                  
                  {billData.pricing?.discount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Diskon</span>
                      <span className="text-sm text-green-600">
                        -{formatCurrency(billData.pricing.discount)}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Pajak (PPN 10%)</span>
                    <span className="text-sm text-gray-900">
                      {formatCurrency(billData.pricing?.tax)}
                    </span>
                  </div>
                  
                  {selectedMethodData?.fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Biaya Admin</span>
                      <span className="text-sm text-gray-900">
                        {formatCurrency(selectedMethodData.fee)}
                      </span>
                    </div>
                  )}
                </div>
                
                <hr className="border-gray-200" />
                
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-900">Total Bayar</span>
                  <span className="text-xl font-bold text-gray-900">
                    {formatCurrency(totalWithFee)}
                  </span>
                </div>
              </div>
              
              <button 
                onClick={processPayment}
                disabled={!selectedMethod || loading}
                className={`w-full mt-6 px-4 py-3 rounded-lg font-medium transition-colors ${
                  selectedMethod && !loading
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Memproses...</span>
                  </div>
                ) : selectedMethod ? (
                  `Bayar dengan ${selectedMethodData?.name}`
                ) : (
                  'Pilih Metode Pembayaran'
                )}
              </button>
              
              {selectedMethodData && (
                <div className="mt-3 text-center">
                  <p className="text-xs text-gray-600">
                    Waktu proses: {selectedMethodData.processingTime}
                  </p>
                  {selectedMethodData.fee > 0 && (
                    <p className="text-xs text-orange-600 mt-1">
                      *Termasuk biaya admin {formatCurrency(selectedMethodData.fee)}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Contact Support */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="font-medium text-gray-900 mb-3">
                Butuh Bantuan?
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Tim customer service siap membantu 24/7
              </p>
              <div className="space-y-2">
                <button className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors">
                  <span>📞</span>
                  <span>0804-1-500-500</span>
                </button>
                <button className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors">
                  <span>💬</span>
                  <span>Live Chat</span>
                </button>
              </div>
            </div>

            {/* Payment Tips */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
              <h4 className="font-medium text-gray-900 mb-3">
                Tips Pembayaran
              </h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start space-x-2">
                  <span className="text-green-600 mt-0.5">•</span>
                  <span>Transfer sesuai nominal exact untuk verifikasi otomatis</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-green-600 mt-0.5">•</span>
                  <span>Simpan bukti pembayaran untuk referensi</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-green-600 mt-0.5">•</span>
                  <span>Hubungi CS jika pembayaran belum terkonfirmasi setelah 1 jam</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PaymentPage;